import React, { useState, useEffect } from 'react';
import { satelliteBoundariesApi, satelliteImageryApi, anomalyApi } from '../../services/api';

interface Boundary {
  id: string;
  name: string;
  area_hectares?: number;
  monitoring_enabled: boolean;
}

interface VegetationIndex {
  index_type: string;
  mean_value: number;
  min_value: number;
  max_value: number;
  std_dev: number;
}

interface ImageryRecord {
  id: string;
  acquisition_date: string;
  satellite: string;
  cloud_cover_percentage: number;
  metadata?: { imageId?: string };
  indices: VegetationIndex[];
}

const INDEX_COLORS: Record<string, string> = {
  NDVI: '#16a34a',
  NDWI: '#2563eb',
  NDBI: '#d97706',
  EVI: '#7c3aed',
  SAVI: '#db2777',
};

// Interpretation ranges for each index
const INDEX_INFO: Record<string, { label: string; description: string; ranges: { label: string; min: number; max: number; color: string }[] }> = {
  NDVI: {
    label: 'Vegetation Health',
    description: 'Normalized Difference Vegetation Index — measures live green vegetation density.',
    ranges: [
      { label: 'Bare / No vegetation', min: -1,  max: 0.1,  color: '#d1d5db' },
      { label: 'Sparse vegetation',    min: 0.1,  max: 0.3,  color: '#fde68a' },
      { label: 'Moderate vegetation',  min: 0.3,  max: 0.6,  color: '#86efac' },
      { label: 'Dense vegetation',     min: 0.6,  max: 1.0,  color: '#15803d' },
    ],
  },
  NDWI: {
    label: 'Water Content',
    description: 'Normalized Difference Water Index — detects water bodies and moisture in vegetation.',
    ranges: [
      { label: 'Dry / No water',   min: -1,  max: 0,   color: '#fde68a' },
      { label: 'Low moisture',     min: 0,   max: 0.2,  color: '#bae6fd' },
      { label: 'High water/moist', min: 0.2, max: 1.0,  color: '#0369a1' },
    ],
  },
  NDBI: {
    label: 'Built-up Index',
    description: 'Normalized Difference Built-up Index — detects urban / built-up areas.',
    ranges: [
      { label: 'Vegetation / Water', min: -1,  max: 0,   color: '#86efac' },
      { label: 'Low built-up',       min: 0,   max: 0.2,  color: '#fde68a' },
      { label: 'High built-up',      min: 0.2, max: 1.0,  color: '#b45309' },
    ],
  },
  EVI: {
    label: 'Enhanced Vegetation',
    description: 'Enhanced Vegetation Index — improved NDVI with reduced atmospheric noise.',
    ranges: [
      { label: 'Sparse / None', min: -1,  max: 0.2,  color: '#fde68a' },
      { label: 'Moderate',      min: 0.2, max: 0.5,  color: '#86efac' },
      { label: 'Dense',         min: 0.5, max: 1.0,  color: '#15803d' },
    ],
  },
};

function ndviInterpretation(value: number): string {
  if (value < 0.1) return 'Bare soil or water';
  if (value < 0.3) return 'Sparse vegetation';
  if (value < 0.6) return 'Moderate vegetation';
  return 'Dense, healthy vegetation';
}

function IndexBadge({ type, value }: { type: string; value: number | string | null }) {
  const color = INDEX_COLORS[type] || '#6b7280';
  const n = Number(value ?? 0);
  const info = INDEX_INFO[type];
  return (
    <span
      title={info ? `${info.label}: ${info.description}` : type}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold text-white cursor-help"
      style={{ backgroundColor: color }}
    >
      {type}: {n.toFixed(3)}
    </span>
  );
}

function CloudBadge({ pct }: { pct: number | string | null }) {
  const n = Number(pct ?? 0);
  const cls =
    n < 10 ? 'bg-green-100 text-green-800'
    : n < 30 ? 'bg-yellow-100 text-yellow-800'
    : 'bg-red-100 text-red-800';
  const title =
    n < 10 ? 'Low cloud cover — excellent image quality'
    : n < 30 ? 'Moderate cloud cover — acceptable quality'
    : 'High cloud cover — image may be obscured';
  return (
    <span title={title} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium cursor-help ${cls}`}>
      ☁ {n.toFixed(1)}%
    </span>
  );
}

// Small tooltip icon
function HelpTip({ text }: { text: string }) {
  return (
    <span
      title={text}
      className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300 text-xs font-bold cursor-help select-none"
    >
      ?
    </span>
  );
}

// Step badge for the workflow guide
function Step({ n, label, active }: { n: number; label: string; active?: boolean }) {
  return (
    <div className={`flex items-center gap-2 ${active ? 'text-blue-700 dark:text-blue-300 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
      <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${active ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'border-gray-300 dark:border-gray-600'}`}>
        {n}
      </span>
      <span className="text-sm">{label}</span>
    </div>
  );
}

export const ImageryPage: React.FC = () => {
  const [boundaries, setBoundaries] = useState<Boundary[]>([]);
  const [selectedBoundaryId, setSelectedBoundaryId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14); // default to 14 days ago (GEE processing lag)
    return d.toISOString().split('T')[0];
  });
  const [maxCloudCover, setMaxCloudCover] = useState(30);

  const [imagery, setImagery] = useState<ImageryRecord[]>([]);
  const [loadingBoundaries, setLoadingBoundaries] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadingImagery, setLoadingImagery] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [changeResult, setChangeResult] = useState<any>(null);
  const [showLegend, setShowLegend] = useState(false);

  const step = !selectedBoundaryId ? 1 : imagery.length === 0 ? 2 : 3;

  useEffect(() => {
    satelliteBoundariesApi
      .getAll({ monitoring_enabled: true })
      .then((res) => setBoundaries(res.data?.boundaries || res.boundaries || []))
      .catch((err: any) => {
        const msg = err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to load boundaries';
        setError(msg);
        setBoundaries([]);
      })
      .finally(() => setLoadingBoundaries(false));
  }, []);

  useEffect(() => {
    if (!selectedBoundaryId) { setImagery([]); return; }
    setLoadingImagery(true);
    satelliteImageryApi
      .getImagery(selectedBoundaryId)
      .then((res) => setImagery(res.imagery || []))
      .catch(() => setImagery([]))
      .finally(() => setLoadingImagery(false));
  }, [selectedBoundaryId]);

  const handleFetch = async () => {
    if (!selectedBoundaryId) { setError('Select a boundary first'); return; }
    setFetching(true); setError(null); setSuccess(null);
    try {
      const res = await satelliteImageryApi.fetchImagery(selectedBoundaryId, { startDate, endDate, maxCloudCover });
      setSuccess(`✅ Fetched ${res.count} image(s) from Google Earth Engine`);
      const stored = await satelliteImageryApi.getImagery(selectedBoundaryId);
      setImagery(stored.imagery || []);
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.details || err.response?.data?.message || err.message || 'Failed to fetch imagery from GEE';
      setError(msg);
    } finally { setFetching(false); }
  };

  const handleDetect = async () => {
    if (!selectedBoundaryId) { setError('Select a boundary first'); return; }
    setDetecting(true); setError(null); setChangeResult(null);
    try {
      const detect = await anomalyApi.detectChanges(selectedBoundaryId);
      setChangeResult(detect);
      setSuccess(detect.anomaly ? `⚠ Anomaly detected — ${detect.anomaly.severity} severity` : '✅ No significant changes detected');
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.details || err.response?.data?.message || err.message || 'Change detection failed';
      setError(msg);
    } finally { setDetecting(false); }
  };

  const handleLoadThumbnail = async (imageryId: string) => {
    if (thumbnailUrls[imageryId]) return;
    try {
      const res = await satelliteImageryApi.getThumbnail(imageryId);
      setThumbnailUrls((prev) => ({ ...prev, [imageryId]: res.thumbnailUrl }));
    } catch {
      setThumbnailUrls((prev) => ({ ...prev, [imageryId]: 'error' }));
    }
  };

  const handleDelete = async (imageryId: string) => {
    if (!confirm('Delete this imagery record?')) return;
    try {
      await satelliteImageryApi.deleteImagery(imageryId);
      setImagery((prev) => prev.filter((i) => i.id !== imageryId));
      setSuccess('Imagery deleted');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete imagery');
    }
  };

  const selectedBoundary = boundaries.find((b) => b.id === selectedBoundaryId);

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Satellite Imagery</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Pull Sentinel-2 imagery from Google Earth Engine and inspect vegetation health indices
          </p>
        </div>
        <button
          onClick={() => setShowLegend((v) => !v)}
          className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center gap-1"
        >
          <span>📖</span> {showLegend ? 'Hide' : 'Show'} Index Legend
        </button>
      </div>

      {/* ── Workflow steps ── */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-3">How to use this page</p>
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <Step n={1} label="Select a monitoring boundary" active={step === 1} />
          <Step n={2} label="Set date range → Fetch Sentinel-2 Imagery" active={step === 2} />
          <Step n={3} label="Review indices & Run Change Detection" active={step === 3} />
        </div>
        <p className="mt-2 text-xs text-blue-500 dark:text-blue-400">
          💡 Imagery is pulled live from Google Earth Engine on demand — results are cached in the database for future views.
          GEE has a ~5–14 day processing lag, so set your End Date at least 2 weeks in the past.
        </p>
      </div>

      {/* ── Index Legend (collapsible) ── */}
      {showLegend && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Vegetation Index Reference</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(INDEX_INFO).map(([key, info]) => (
              <div key={key} className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: INDEX_COLORS[key] }} />
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{key} — {info.label}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{info.description}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {info.ranges.map((r) => (
                    <span key={r.label} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600">
                      <span className="inline-block w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: r.color }} />
                      {r.min.toFixed(1)}–{r.max.toFixed(1)} {r.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Alerts ── */}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 flex justify-between items-start">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-4 text-lg leading-none">&times;</button>
        </div>
      )}
      {success && (
        <div className="rounded-md bg-green-50 border border-green-200 p-4 flex justify-between items-start">
          <p className="text-sm text-green-800">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700 ml-4 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* ── Controls ── */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold flex-shrink-0">
            {step <= 2 ? step : '✓'}
          </span>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Fetch Imagery from GEE</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          {/* Boundary */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Boundary
              <HelpTip text="Select the monitoring boundary you drew on the Boundaries page. Only boundaries with monitoring enabled appear here." />
            </label>
            {loadingBoundaries ? (
              <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
            ) : boundaries.length === 0 ? (
              <div className="h-10 flex items-center px-3 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-700">
                No boundaries found — go to <strong className="mx-1">Boundaries</strong> page first
              </div>
            ) : (
              <select
                value={selectedBoundaryId}
                onChange={(e) => setSelectedBoundaryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select a boundary —</option>
                {boundaries.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} {b.area_hectares ? `(${Number(b.area_hectares).toFixed(1)} ha)` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Start date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date
              <HelpTip text="Beginning of the search window. Use a date at least 3 months in the past for best results." />
            </label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* End date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date
              <HelpTip text="End of the search window. Must be at least 14 days in the past — GEE imagery takes ~5–14 days to process." />
            </label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* Cloud cover */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Max Cloud Cover: {maxCloudCover}%
              <HelpTip text="Only include images with this cloud cover % or less. Increase to 50–80% during monsoon season." />
            </label>
            <input type="range" min={0} max={100} value={maxCloudCover}
              onChange={(e) => setMaxCloudCover(Number(e.target.value))}
              className="w-full accent-blue-600" />
            <div className="flex justify-between text-xs text-gray-400 mt-0.5">
              <span>0% (clear)</span><span>100% (any)</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5 items-center">
          <button onClick={handleFetch} disabled={fetching || !selectedBoundaryId}
            className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {fetching
              ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Fetching from GEE…</>
              : '🛰 Fetch Sentinel-2 Imagery'
            }
          </button>

          <button onClick={handleDetect} disabled={detecting || !selectedBoundaryId}
            className="px-5 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {detecting
              ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Detecting…</>
              : '🔍 Run Change Detection'
            }
          </button>

          <p className="text-xs text-gray-400 dark:text-gray-500 ml-1">
            Change detection compares NDVI from two GEE periods — independent of stored imagery above
          </p>
        </div>
      </div>

      {/* ── Change detection result ── */}
      {changeResult && !changeResult.anomaly && (
        <div className="bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-700 rounded-lg p-4 flex items-start gap-3">
          <span className="text-2xl">✅</span>
          <div>
            <p className="font-semibold text-green-800 dark:text-green-300">No significant changes detected</p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
              NDVI change was below the 15% threshold. Vegetation health appears stable between the baseline and current periods.
            </p>
          </div>
        </div>
      )}

      {changeResult?.anomaly && (
        <div className="bg-orange-50 border border-orange-200 dark:bg-orange-900/20 dark:border-orange-700 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">⚠</span>
            <h3 className="font-semibold text-orange-800 dark:text-orange-300">Anomaly Detected</h3>
            <span className="ml-auto text-xs text-orange-600 dark:text-orange-400">Saved to Anomaly Dashboard →</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-xs text-gray-500 block">Severity
                <HelpTip text="low &lt;10% · medium &lt;20% · high &lt;30% · critical ≥30% NDVI change" />
              </span>
              <p className="font-bold capitalize text-orange-700 dark:text-orange-300 mt-0.5">{changeResult.anomaly.severity}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500 block">NDVI Change
                <HelpTip text="Percentage change in mean NDVI between baseline and current period. Negative = vegetation decline." />
              </span>
              <p className="font-bold text-orange-700 dark:text-orange-300 mt-0.5">{Number(changeResult.anomaly.changePercentage ?? 0).toFixed(1)}%</p>
            </div>
            <div>
              <span className="text-xs text-gray-500 block">Affected Area</span>
              <p className="font-bold text-gray-900 dark:text-white mt-0.5">{Number(changeResult.anomaly.affectedArea ?? 0).toFixed(1)} ha</p>
            </div>
            <div>
              <span className="text-xs text-gray-500 block">Detected</span>
              <p className="font-bold text-gray-900 dark:text-white mt-0.5">{new Date(changeResult.anomaly.detectedAt).toLocaleDateString()}</p>
            </div>
          </div>
          {changeResult.anomaly.description && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 italic border-t border-orange-200 dark:border-orange-700 pt-3">
              {changeResult.anomaly.description}
            </p>
          )}
        </div>
      )}

      {/* ── Stored Imagery list ── */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Stored Imagery
              {selectedBoundary && <span className="ml-2 text-sm font-normal text-gray-500">— {selectedBoundary.name}</span>}
            </h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Images cached from previous GEE fetches. Click <strong>Load RGB</strong> to render a true-colour thumbnail.
            </p>
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0">{imagery.length} record{imagery.length !== 1 ? 's' : ''}</span>
        </div>

        {loadingImagery ? (
          <div className="p-10 flex justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          </div>
        ) : !selectedBoundaryId ? (
          <div className="p-10 text-center">
            <p className="text-4xl mb-2">☝</p>
            <p className="text-gray-400 text-sm">Select a boundary above to view stored imagery</p>
          </div>
        ) : imagery.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-4xl mb-2">🛰</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">No imagery stored yet</p>
            <p className="text-gray-400 text-xs mt-1">Use <strong>Fetch Sentinel-2 Imagery</strong> above to pull data from Google Earth Engine</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {imagery.map((img) => (
              <div key={img.id} className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {new Date(img.acquisition_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{img.satellite}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <CloudBadge pct={img.cloud_cover_percentage} />
                    {(img.indices || []).map((idx) => (
                      <IndexBadge key={idx.index_type} type={idx.index_type} value={idx.mean_value} />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleLoadThumbnail(img.id)}
                      title="Load a true-colour RGB preview of this Sentinel-2 image from GEE"
                      className="text-xs px-3 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                      {thumbnailUrls[img.id] === 'error' ? '⚠ No thumb' : thumbnailUrls[img.id] ? '✓ Loaded' : 'Load RGB'}
                    </button>
                    <button onClick={() => handleDelete(img.id)}
                      title="Delete this imagery record from the database (does not affect GEE)"
                      className="text-xs px-3 py-1 border border-red-200 rounded hover:bg-red-50 text-red-600">
                      Delete
                    </button>
                  </div>
                </div>

                {/* RGB thumbnail */}
                {thumbnailUrls[img.id] && thumbnailUrls[img.id] !== 'error' && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-1">True-colour RGB (Bands 4-3-2)</p>
                    <img src={thumbnailUrls[img.id]} alt={`Sentinel-2 RGB ${img.acquisition_date}`}
                      className="rounded border border-gray-200 dark:border-gray-600 max-h-48 object-cover" />
                  </div>
                )}

                {/* Index breakdown */}
                {img.indices?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-1.5">Vegetation indices (mean value over boundary)</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {img.indices.map((idx) => {
                        const mean = Number(idx.mean_value ?? 0);
                        const interpretation = idx.index_type === 'NDVI' ? ndviInterpretation(mean) : '';
                        return (
                          <div key={idx.index_type}
                            title={INDEX_INFO[idx.index_type]?.description || idx.index_type}
                            className="bg-gray-50 dark:bg-gray-700/50 rounded p-2 cursor-help">
                            <p className="text-xs font-semibold" style={{ color: INDEX_COLORS[idx.index_type] || '#6b7280' }}>
                              {idx.index_type}
                            </p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">
                              {mean.toFixed(3)}
                            </p>
                            <p className="text-xs text-gray-400">
                              min {Number(idx.min_value ?? 0).toFixed(3)} · max {Number(idx.max_value ?? 0).toFixed(3)}
                            </p>
                            {interpretation && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 italic">{interpretation}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Made with Bob
